import {
  FamilyMember,
  FamilyTreeProgress,
  GenerationProgress,
  REQUIRED_FIELDS,
  OPTIONAL_FIELDS,
} from "./familyTreeUtils";
import { getParents } from "./familyTreeMigrationUtils";

/**
 * Check if a field value is considered "filled" (not template/placeholder)
 */
function isFieldFilled(value: string | undefined): boolean {
  if (!value) return false;
  if (value.trim() === "") return false;

  // Check for common placeholder patterns
  const placeholders = [
    "YYYY-MM-DD",
    "Your Name",
    "Father's Name",
    "Mother's Name",
    "Spouse Name",
    "Placeholder for image metadata",
  ];

  return !placeholders.some((placeholder) =>
    value.toLowerCase().includes(placeholder.toLowerCase())
  );
}

/**
 * Check if a member has a valid uploaded image (not placeholder)
 */
export function hasValidImage(imageMetadata: string | undefined): boolean {
  if (!imageMetadata) return false;

  // Check if it's a placeholder or actual image data
  const imagePlaceholders = [
    "Placeholder for image metadata",
    "placeholder",
    "your image",
    "add image",
    "upload image",
    "image placeholder",
    "temp image",
    "no image",
    "default image",
  ];

  return !imagePlaceholders.some((placeholder) =>
    imageMetadata.toLowerCase().includes(placeholder.toLowerCase())
  );
}

/**
 * Calculate completion score for a single family member
 * Returns 0-100 based on filled required and optional fields
 */
export function calculateMemberCompletionScore(member: FamilyMember): number {
  let filledRequired = 0;
  let filledOptional = 0;

  // Check required fields
  REQUIRED_FIELDS.forEach((field) => {
    if (isFieldFilled(member[field as keyof FamilyMember] as string)) {
      filledRequired++;
    }
  });

  // Check optional fields
  OPTIONAL_FIELDS.forEach((field) => {
    if (isFieldFilled(member[field as keyof FamilyMember] as string)) {
      filledOptional++;
    }
  });

  // Calculate percentage: Required fields are more important
  const requiredPercentage = (filledRequired / REQUIRED_FIELDS.length) * 80;
  const optionalPercentage = (filledOptional / OPTIONAL_FIELDS.length) * 20;

  return Math.round(requiredPercentage + optionalPercentage);
}

/**
 * Determine edit status based on completion score and field analysis
 */
export function calculateEditStatus(
  member: FamilyMember
): "incomplete" | "partial" | "complete" {
  const score = calculateMemberCompletionScore(member);

  // Check if all required fields are filled
  const allRequiredFilled = REQUIRED_FIELDS.every((field) =>
    isFieldFilled(member[field as keyof FamilyMember] as string)
  );

  if (allRequiredFilled) {
    return "complete";
  } else if (score > 0) {
    return "partial";
  } else {
    return "incomplete";
  }
}

/**
 * Update a family member's progress tracking fields
 */
export function updateMemberProgress(member: FamilyMember): FamilyMember {
  const completionScore = calculateMemberCompletionScore(member);
  const editStatus = calculateEditStatus(member);

  return {
    ...member,
    completionScore,
    editStatus,
    lastModified: new Date().toISOString(),
  };
}

/**
 * Get generation number for a family member based on their position in the tree
 */
function getMemberGeneration(
  memberId: string,
  familyTree: FamilyMember[],
  currentGeneration: number = 0
): number {
  for (const member of familyTree) {
    if (member.id === memberId) {
      return currentGeneration;
    }
    const parents = getParents(member);
    if (parents.length > 0) {
      const parentGeneration = getMemberGeneration(
        memberId,
        parents,
        currentGeneration + 1
      );
      if (parentGeneration !== -1) {
        return parentGeneration;
      }
    }
  }
  return -1;
}

/**
 * Get all family members organized by generation
 */
export function getMembersByGeneration(
  familyTree: FamilyMember[]
): Map<number, FamilyMember[]> {
  const membersByGeneration = new Map<number, FamilyMember[]>();

  function traverseTree(members: FamilyMember[], generation: number) {
    members.forEach((member) => {
      if (!membersByGeneration.has(generation)) {
        membersByGeneration.set(generation, []);
      }
      membersByGeneration.get(generation)!.push(member);

      const parents = getParents(member);
      if (parents.length > 0 && generation < 5) {
        // Limit to 5 generations
        traverseTree(parents, generation + 1);
      }
    });
  }

  traverseTree(familyTree, 0);
  return membersByGeneration;
}

/**
 * Calculate expected number of members for each generation
 */
function getExpectedMembersForGeneration(generation: number): number {
  // Generation 0 (self + spouse): 2 people (self and spouse)
  // Generation 1 (parents): 4 people (2 for user + 2 for spouse)
  // Generation 2 (grandparents): 8 people (4 for user + 4 for spouse)
  // Generation 3 (great-grandparents): 16 people (8 for user + 8 for spouse)
  // etc.
  if (generation === 0) {
    return 2; // Self + spouse
  }
  // For generations 1+, we have both the user's and spouse's family lines
  // So it's 2 * 2^generation = 2^(generation+1)
  return Math.pow(2, generation + 1);
}

/**
 * Calculate progress for each generation
 */
export function calculateGenerationProgress(
  familyTree: FamilyMember[]
): GenerationProgress[] {
  const membersByGeneration = getMembersByGeneration(familyTree);
  const generationProgress: GenerationProgress[] = [];

  // Calculate for up to 5 generations (0-4)
  for (let generation = 0; generation < 5; generation++) {
    const members = membersByGeneration.get(generation) || [];
    const totalSlots = getExpectedMembersForGeneration(generation);

    let incompleteSlots = 0;
    let partialSlots = 0;
    let completeSlots = 0;
    let uploadedImages = 0;

    members.forEach((member) => {
      const status = member.editStatus || calculateEditStatus(member);
      switch (status) {
        case "incomplete":
          incompleteSlots++;
          break;
        case "partial":
          partialSlots++;
          break;
        case "complete":
          completeSlots++;
          break;
      }

      // Track image uploads for this generation
      if (hasValidImage(member.imageMetadata)) {
        uploadedImages++;
      }
    });

    const filledSlots = members.length;
    const completionPercentage =
      totalSlots > 0 ? Math.round((completeSlots / totalSlots) * 100) : 0;

    // Image tracking for this generation
    const totalImages = totalSlots; // Each slot should have an image
    const missingImages = totalImages - uploadedImages;
    const imageCompletionPercentage =
      totalImages > 0 ? Math.round((uploadedImages / totalImages) * 100) : 0;

    generationProgress.push({
      generation,
      totalSlots,
      filledSlots,
      incompleteSlots,
      partialSlots,
      completeSlots,
      completionPercentage,
      totalImages,
      uploadedImages,
      missingImages,
      imageCompletionPercentage,
    });
  }

  return generationProgress;
}

/**
 * Calculate overall family tree progress
 */
export function calculateOverallProgress(
  familyTree: FamilyMember[]
): FamilyTreeProgress {
  let totalMembers = 0;
  let incompleteMembers = 0;
  let partialMembers = 0;
  let completeMembers = 0;
  let customMembers = 0;
  let totalScore = 0;
  let lastActivity: string | undefined;

  // Image tracking
  let totalImages = 0;
  let uploadedImages = 0;

  function traverseMembers(members: FamilyMember[]) {
    members.forEach((member) => {
      totalMembers++;
      totalImages++; // Each member should have an image

      const status = member.editStatus || calculateEditStatus(member);
      const score =
        member.completionScore || calculateMemberCompletionScore(member);

      totalScore += score;

      // Track image uploads
      if (hasValidImage(member.imageMetadata)) {
        uploadedImages++;
      }

      switch (status) {
        case "incomplete":
          incompleteMembers++;
          break;
        case "partial":
          partialMembers++;
          break;
        case "complete":
          completeMembers++;
          break;
      }

      if (member.isCustomAdded) {
        customMembers++;
      }

      // Track most recent activity
      if (member.lastModified) {
        if (!lastActivity || member.lastModified > lastActivity) {
          lastActivity = member.lastModified;
        }
      }

      const parents = getParents(member);
      if (parents.length > 0) {
        traverseMembers(parents);
      }
    });
  }

  traverseMembers(familyTree);

  const generationProgress = calculateGenerationProgress(familyTree);
  const overallCompletion =
    totalMembers > 0 ? Math.round((completeMembers / totalMembers) * 100) : 0;
  const dataQualityScore =
    totalMembers > 0 ? Math.round(totalScore / totalMembers) : 0;

  const missingImages = totalImages - uploadedImages;

  return {
    totalMembers,
    incompleteMembers,
    partialMembers,
    completeMembers,
    customMembers,
    totalImages,
    uploadedImages,
    missingImages,
    generationProgress,
    overallCompletion,
    dataQualityScore,
    lastActivity,
  };
}

/**
 * Migrate existing family tree data to include progress tracking fields
 */
export function migrateFamilyTreeData(
  familyTree: FamilyMember[]
): FamilyMember[] {
  function migrateMembers(members: FamilyMember[]): FamilyMember[] {
    return members.map((member) => {
      const migratedMember: FamilyMember = {
        ...member,
        editStatus: member.editStatus || calculateEditStatus(member),
        completionScore:
          member.completionScore || calculateMemberCompletionScore(member),
        isCustomAdded: member.isCustomAdded || false,
      };

      const parents = getParents(member);
      if (parents.length > 0) {
        migratedMember.parents = migrateMembers(parents);
        // Remove old children field to complete migration
        migratedMember.children = undefined;
      }

      return migratedMember;
    });
  }

  return migrateMembers(familyTree);
}

/**
 * Get suggestions for improving family tree completion
 */
export function getCompletionSuggestions(familyTree: FamilyMember[]): string[] {
  const suggestions: string[] = [];
  const progress = calculateOverallProgress(familyTree);

  if (progress.incompleteMembers > 0) {
    suggestions.push(
      `You have ${progress.incompleteMembers} family members with incomplete information. Start by adding their real names and birth dates.`
    );
  }

  if (progress.partialMembers > 0) {
    suggestions.push(
      `${progress.partialMembers} family members are partially complete. Add missing information to improve your family tree.`
    );
  }

  // Check for incomplete generations
  progress.generationProgress.forEach((gen, index) => {
    if (gen.filledSlots < gen.totalSlots && index < 3) {
      // Focus on first 3 generations
      const generationNames = [
        "you & spouse",
        "parents",
        "grandparents",
        "great-grandparents",
        "great-great-grandparents",
      ];
      suggestions.push(
        `Add missing ${generationNames[gen.generation]} (${gen.filledSlots}/${
          gen.totalSlots
        } added).`
      );
    }
  });

  return suggestions;
}
